import {
	ParkingPrebookingAvailabilityEntry,
	SelectedParkingPrebookingAvailabilityEntry
} from "../types/ParkingPrebookingAvailabilityEntry";
import {PaymentMethod} from "../index";
import {TariffId} from "../nominalTypes/TariffId";
import {ZoneId} from "../nominalTypes/ZoneId";
import {GetParkingOperationStepsArguments} from "../types/arguments/GetParkingOperationStepsArguments";
import {MultiOperationPermit} from "./MultiOperationPermit";

export class DailyWeatherPermit {
	private _availability: ParkingPrebookingAvailabilityEntry[];
	private _operations: MultiOperationPermit;

	constructor() {
		this._availability = [];
		this._operations = new MultiOperationPermit(this._availability, new Set());
	}

	public get availability(): ParkingPrebookingAvailabilityEntry[] {
		return this._availability;
	}

	public update(availability: ParkingPrebookingAvailabilityEntry[]): void {
		this._availability = availability;
		this._operations = new MultiOperationPermit(availability, new Set());
	}

	/**
	 * Immutable variant of {@link setSelectedEntries}: returns a new permit with the same
	 * availability and the given selection. MVVM state holds this class as an opaque leaf,
	 * so changes must be signaled by replacing the instance, not by mutating it.
	 */
	public withSelectedEntries(selected: number[]): DailyWeatherPermit {
		const next = new DailyWeatherPermit();
		next.update(this._availability);
		next.setSelectedEntries(selected);
		return next;
	}

	public setSelectedEntries(selected: number[]): void {
		this._operations.clear();

		for (const index of selected) {
			const entry = this._availability[index];
			if (entry) {
				this._operations.tryCreateSegment(entry.startDate, entry.endDate);
			}
		}
	}

	public isValid(): boolean {
		return this._operations.size > 0;
	}

	public getSelected(): SelectedParkingPrebookingAvailabilityEntry[] {
		return this._operations.getSelected();
	}

	public getParkingOperationArguments(tariffId: TariffId, zoneId: ZoneId, plates: string[][]): GetParkingOperationStepsArguments {
		const selected = this.getSelected();
		if (selected.length === 0) {
			throw new Error("getParkingOperationArguments requires a non-empty selection: check isValid() first");
		}

		return {
			startDate: selected[0].startDate,
			endDate: selected.at(-1)!.endDate,
			tariffId: tariffId,
			branch: {zoneId: zoneId},
			deferConfirmation: false,
			licensePlates: plates,
			paymentMethod: PaymentMethod.Undefined,
			preBookingEntries: this._availability,
			selectedPreBookingEntries: selected
		}
	}
}
